import type { CustomAttributeV2Fragment } from '~~/lib/Shop/generated/schema';
import { convertContentfulToWebappLocaleCode } from '~/utils/convertContentfulToWebappLocaleCode';
import { isDefined } from '~/utils/guards/isDefined';
import { isAttributeSelectedOptions } from '~/utils/shop/guards/isAttributeSelectedOptions';

const BASLER_REGION_AVAILABILITY = 'basler_region_aviability';

const STORE_REGION_MAP: Record<string, string[]> = {
  amer_en: ['amer'],
  emea_de: ['emea'],
  emea_fr: ['france'],
  emea_it: ['italy'],
  emea_ru: ['russia'],
  emea_in: ['india'],
  emea_en: ['emea'],
  apac_en: ['apac'],
  apac_ko: ['korea'],
  apac_jp: ['japan'],
  apac_zhtw: ['apac'],
  china_cn: ['china']
};

type GetPdpAlternateLocalesOptions = {
  locales: string[];
  currentLocale: string;
  customAttributes: ReadonlyArray<CustomAttributeV2Fragment | null | undefined>;
  localeStoreMap?: Record<string, string>;
};

function normalizeToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function getRegionCandidatesForStore(store: string): string[] {
  const normalizedStore = normalizeToken(store);
  const defaultCandidates = [normalizedStore];

  return [...defaultCandidates, ...(STORE_REGION_MAP[normalizedStore] ?? [])];
}

function getProductRegionAvailability(
  customAttributes: ReadonlyArray<CustomAttributeV2Fragment | null | undefined>
): string[] | null {
  const regionAvailability = customAttributes
    .filter(isDefined)
    .find((attribute) => attribute.code === BASLER_REGION_AVAILABILITY);

  if (!isAttributeSelectedOptions(regionAvailability)) {
    return null;
  }

  const availableRegions = regionAvailability.selected_options
    .filter(isDefined)
    .map((option) => normalizeToken(option.value))
    .filter((value) => value.length > 0);

  return availableRegions.length > 0 ? availableRegions : null;
}

export function getPdpAlternateLocales({
  locales,
  currentLocale,
  customAttributes,
  localeStoreMap = {}
}: GetPdpAlternateLocalesOptions): string[] {
  const availableRegions = getProductRegionAvailability(customAttributes);
  if (!availableRegions) {
    return locales;
  }

  const normalizedCurrentLocale = currentLocale.toLowerCase();

  return locales.filter((localeCode) => {
    const normalizedLocaleCode = localeCode.toLowerCase();
    const webappLocaleCode = convertContentfulToWebappLocaleCode(localeCode);
    if (
      normalizedLocaleCode === normalizedCurrentLocale ||
      webappLocaleCode === normalizedCurrentLocale
    ) {
      return true;
    }

    const store =
      localeStoreMap[webappLocaleCode] ?? localeStoreMap[localeCode];
    if (!store) {
      return false;
    }

    const candidates = getRegionCandidatesForStore(store);
    return candidates.some((candidate) => availableRegions.includes(candidate));
  });
}
